import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Section, Card } from '../shared/primitives';
import { Segmented, Field, Result, Note, Bullets, StepRow, AboutCard, References, SourceRow } from '../shared/protocolBits';
import {
  MODES, RENAL, EXTRA_BOLUS, INFUSION_STEP, STEPS, RESCUE, DRUG_FACTS, ALTERNATIVES,
  BACKGROUND, PK_TABLE, REFERENCES, BIVAL_PDF, BIVAL_UPDATED,
} from '../../../data/hospitals/thiBivalirudin';

const ACCENT = '#9F8D70';
const SESSION_KEY = 'thi-bival-calc';

const num = (v) => (v === '' || v === null || v === undefined ? NaN : parseFloat(v));
const fmt = (n, dp = 1) => (Number.isFinite(n) ? Number(n.toFixed(dp)).toString() : '—');
const range = (a, b, unit, dp = 1) => `${fmt(a, dp)}–${fmt(b, dp)} ${unit}`;
const clock = (ts) => new Date(ts).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const load = () => {
  try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '{}'); } catch { return {}; }
};

/**
 * Bivalirudin guide: dosing calculator, ACT log, then the protocol step by step.
 * Calculator state lives in sessionStorage so it survives leaving and returning
 * during a case, and clears when the app is closed.
 */
const BivalirudinGuide = () => {
  const saved = load();
  const [mode, setMode] = useState(saved.mode || 'on-pump');
  const [weight, setWeight] = useState(saved.weight || '');
  const [renal, setRenal] = useState(saved.renal || 'normal');
  const [baseline, setBaseline] = useState(saved.baseline || '');
  const [infusionNow, setInfusionNow] = useState(saved.infusionNow || '');
  const [readings, setReadings] = useState(saved.readings || []); // [{ t, v }]
  const [draft, setDraft] = useState('');

  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ mode, weight, renal, baseline, infusionNow, readings })); } catch { /* ignore */ }
  }, [mode, weight, renal, baseline, infusionNow, readings]);

  const m = MODES.find((x) => x.id === mode);
  const r = RENAL.find((x) => x.id === renal);
  const w = num(weight);
  const rate = r.rate[mode];
  const adjusted = r.id !== 'normal';

  // ACT target and latest status
  const base = num(baseline);
  const target = m.actTarget.kind === 'fixed' ? m.actTarget.seconds : (Number.isFinite(base) ? base * m.actTarget.factor : NaN);
  const latest = readings[readings.length - 1];
  const status = latest && Number.isFinite(target) ? (latest.v >= target ? 'ok' : 'low') : null;
  const inf = num(infusionNow);

  const addReading = () => {
    const v = num(draft);
    if (!Number.isFinite(v) || v <= 0) return;
    setReadings((prev) => [...prev, { t: Date.now(), v }]);
    setDraft('');
  };
  const clearLog = () => { setReadings([]); };

  const stepLow = w ? range(w * INFUSION_STEP.min, w * INFUSION_STEP.max, 'mg/hr') : `${INFUSION_STEP.min}–${INFUSION_STEP.max} mg/kg/hr`;
  const bolusLow = w ? range(w * EXTRA_BOLUS.min, w * EXTRA_BOLUS.max, 'mg', 0) : `${EXTRA_BOLUS.min}–${EXTRA_BOLUS.max} mg/kg`;
  const newRateUp = w && Number.isFinite(inf) ? range(inf + w * INFUSION_STEP.min, inf + w * INFUSION_STEP.max, 'mg/hr') : null;
  const newRateDown = w && Number.isFinite(inf) ? range(Math.max(0, inf - w * INFUSION_STEP.max), Math.max(0, inf - w * INFUSION_STEP.min), 'mg/hr') : null;

  return (
    <div className="space-y-6">
      <Segmented items={MODES} value={mode} onChange={setMode} />

      <Section title="Dosing">
        <Card>
          <div className="px-4 py-3 space-y-3">
            <Field label="Weight" unit="kg" value={weight} onChange={setWeight} placeholder="70" accent={ACCENT} />
            <div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Renal function</span>
              <div className="mt-1"><Segmented small items={RENAL} value={renal} onChange={setRenal} /></div>
            </div>
          </div>
          <Result label="Bolus" value={w ? `${fmt(w * m.bolus, 0)} mg` : `${m.bolus} mg/kg`} detail={w ? `${m.bolus} mg/kg` : null} />
          <Result
            label="Infusion"
            value={w ? `${fmt(w * rate)} mg/hr` : `${rate} mg/kg/hr`}
            detail={`${w ? `${rate} mg/kg/hr · ` : ''}${r.note}`}
            tone={adjusted ? 'warn' : undefined}
          />
          {m.prime && <Result label="CPB prime" value={`${m.prime} mg`} />}
          <Result label="Extra bolus if sub-therapeutic" value={bolusLow} detail={w ? `${EXTRA_BOLUS.min}–${EXTRA_BOLUS.max} mg/kg` : null} />
          <Result label="Infusion step, up or down" value={stepLow} detail={w ? `${INFUSION_STEP.min}–${INFUSION_STEP.max} mg/kg/hr` : null} />
          {r.warn && <Note tone="danger">{r.warn}.</Note>}
          {!w && <Note>Enter a weight to see doses in mg.</Note>}
        </Card>
      </Section>

      <Section
        title="ACT log"
        aside={readings.length > 0 && (
          <button onClick={clearLog} className="text-xs text-gray-400 hover:text-gray-600 inline-flex items-center gap-1"><Trash2 size={12} /> Clear</button>
        )}
      >
        <Card>
          <div className="px-4 py-3 space-y-3">
            {m.actTarget.kind === 'baseline' && (
              <Field label="Baseline ACT" unit="s" value={baseline} onChange={setBaseline} placeholder="120" accent={ACCENT} />
            )}
            <Field label="Current infusion" unit="mg/hr" value={infusionNow} onChange={setInfusionNow} placeholder={w ? fmt(w * rate) : '175'} accent={ACCENT} optional />
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Field label="New ACT" unit="s" value={draft} onChange={setDraft} placeholder="Add a reading" accent={ACCENT} />
              </div>
              <button
                onClick={addReading}
                disabled={!Number.isFinite(num(draft))}
                aria-label="Add ACT reading"
                className="h-[46px] w-[46px] rounded-xl flex items-center justify-center text-white disabled:opacity-40"
                style={{ backgroundColor: ACCENT }}
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          <Result label="Target" value={Number.isFinite(target) ? `≥ ${fmt(target, 0)} s` : m.actTarget.text} detail={m.actTarget.kind === 'baseline' && Number.isFinite(target) ? m.actTarget.text : null} />

          {latest && status === 'low' && (
            <Note tone="danger">
              <span className="font-semibold">Sub-therapeutic.</span> Bolus {bolusLow} and increase the infusion by {stepLow}.
              {newRateUp && <span className="block mt-1">New rate {newRateUp}.</span>}
            </Note>
          )}
          {latest && status === 'ok' && (
            <Note>
              <span className="font-semibold">Therapeutic.</span> Continue. If well above target, consider decreasing the infusion by {stepLow}.
              {newRateDown && <span className="block mt-1">That would be {newRateDown}.</span>}
            </Note>
          )}
          {latest && status === null && <Note tone="warn">Enter the baseline ACT to set the target.</Note>}

          {readings.length > 0 && (
            <div className="px-4 py-2">
              <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
                {[...readings].reverse().map((x, i, arr) => {
                  const prev = arr[i + 1];
                  const delta = prev ? x.v - prev.v : null;
                  const ok = Number.isFinite(target) ? x.v >= target : null;
                  return (
                    <li key={x.t} className="py-2 flex items-center gap-3 text-sm">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ok === null ? '#d1d5db' : ok ? '#22c55e' : '#ef4444' }} aria-hidden="true" />
                      <span className="w-16 text-gray-500 dark:text-gray-400">{clock(x.t)}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{x.v} s</span>
                      {delta !== null && <span className={`text-xs ${delta < 0 ? 'text-red-600 dark:text-red-300' : 'text-gray-400'}`}>{delta > 0 ? '+' : ''}{delta}</span>}
                      {i === 0 && <span className="ml-auto text-xs text-gray-400">next check in 15–30 min</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {readings.length === 0 && <Note>Check the ACT 5 minutes after the bolus, then every 15–30 minutes on bypass.</Note>}
        </Card>
      </Section>

      {STEPS[mode].map((sec) => (
        <Section key={sec.title} title={sec.title}>
          <Card>
            {sec.groups.map((g, i) => <StepRow key={g.title || i} title={g.title} items={g.items} />)}
          </Card>
        </Section>
      ))}

      <Section title={RESCUE.title}>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl px-4 py-3">
          <Bullets items={RESCUE.items} />
          <p className="mt-2 text-sm font-semibold text-red-700 dark:text-red-300">{RESCUE.warning}</p>
        </div>
      </Section>

      <Section title="Drug facts">
        <Card>{DRUG_FACTS.map((g) => <StepRow key={g.title} title={g.title} items={g.items} />)}</Card>
      </Section>

      <Section title={ALTERNATIVES.title}>
        <Card><StepRow items={ALTERNATIVES.items} /></Card>
      </Section>

      <AboutCard title="About bivalirudin" accent={ACCENT}>
        {BACKGROUND.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
        <div className="pt-1">
          <p className="eyebrow mb-1.5">Pharmacokinetics by renal function</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 font-medium">GFR, mL/min</th>
                <th className="py-1 font-medium text-right">Clearance, mL/min/kg</th>
                <th className="py-1 font-medium text-right">Half-life</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/70">
              {PK_TABLE.map((row) => (
                <tr key={row.fn}>
                  <td className="py-1.5 text-gray-700 dark:text-gray-300">{row.fn}</td>
                  <td className="py-1.5 text-right text-gray-900 dark:text-white">{row.clearance}</td>
                  <td className="py-1.5 text-right text-gray-900 dark:text-white">{row.half}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AboutCard>

      <References items={REFERENCES} />
      <SourceRow href={BIVAL_PDF} updated={BIVAL_UPDATED} accent={ACCENT} />
    </div>
  );
};

export default BivalirudinGuide;
